import React, { useState, useRef, useEffect } from 'react';
import { FaTimes, FaUpload, FaFileExcel, FaTrash, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { collection, writeBatch, doc } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { db } from '../../config/firebaseConfig';
import { splitDuplicateRows } from './budgetImportDedupe';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './AddIncomeModal.module.css';

// Some e-Fatura portals export "Excel" files that are actually HTML tables.
// SheetJS parses them but leaves HTML entities (e.g. &#231; for ç) in cell values.
const decodeHtmlEntities = (val) => {
  if (typeof val !== 'string') return val;
  if (val.indexOf('&') === -1) return val;
  // SheetJS' HTML parser can double-encode (e.g. &amp;#231;), so loop until stable.
  let out = val;
  for (let i = 0; i < 4; i++) {
    const next = out
      .replace(/&amp;/gi, '&')
      .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
      .replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(parseInt(dec, 10)))
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&apos;/gi, "'")
      .replace(/&nbsp;/gi, ' ');
    if (next === out) break;
    out = next;
  }
  return out;
};

const parseAmount = (val) => {
  if (val === null || val === undefined || val === '') return 0;
  if (typeof val === 'number') return val;
  const s = String(decodeHtmlEntities(val)).trim().replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
};

const parseTime = (val) => {
  if (val === null || val === undefined || val === '') return '';
  if (val instanceof Date && !isNaN(val)) {
    const h = String(val.getHours()).padStart(2, '0');
    const m = String(val.getMinutes()).padStart(2, '0');
    const s = String(val.getSeconds()).padStart(2, '0');
    return `${h}:${m}:${s}`;
  }
  if (typeof val === 'number' && val >= 0 && val < 1) {
    const total = Math.round(val * 86400);
    const h = String(Math.floor(total / 3600) % 24).padStart(2, '0');
    const m = String(Math.floor(total / 60) % 60).padStart(2, '0');
    const s = String(total % 60).padStart(2, '0');
    return `${h}:${m}:${s}`;
  }
  return String(val).trim();
};

const parseDate = (val) => {
  if (!val && val !== 0) return '';
  if (val instanceof Date && !isNaN(val)) {
    const y = val.getFullYear();
    const m = String(val.getMonth() + 1).padStart(2, '0');
    const d = String(val.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (typeof val === 'number') {
    const parsed = XLSX.SSF.parse_date_code(val);
    if (parsed) {
      const y = parsed.y;
      const m = String(parsed.m).padStart(2, '0');
      const d = String(parsed.d).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  }
  const s = String(val).trim();
  const m1 = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (m1) {
    return `${m1[3]}-${m1[2].padStart(2, '0')}-${m1[1].padStart(2, '0')}`;
  }
  const m2 = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m2) return s;
  return '';
};

const ImportExpenseExcelModal = ({ isOpen, onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [isChecking, setIsChecking] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState({ existing: 0, inFile: 0 });
  const fileInputRef = useRef(null);

  const resetState = () => {
    setFile(null);
    setRows([]);
    setParseError('');
    setProgress({ done: 0, total: 0 });
    setDuplicateInfo({ existing: 0, inFile: 0 });
  };

  useEffect(() => {
    if (!isOpen) resetState();
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const readWorkbook = async (f) => {
    const buf = await f.arrayBuffer();
    const bytes = new Uint8Array(buf);
    const isZip = bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4B;
    if (isZip) {
      return XLSX.read(buf, { type: 'array', cellDates: true });
    }
    let encoding = 'utf-8';
    if (bytes.length >= 2 && bytes[0] === 0xFF && bytes[1] === 0xFE) encoding = 'utf-16le';
    else if (bytes.length >= 2 && bytes[0] === 0xFE && bytes[1] === 0xFF) encoding = 'utf-16be';
    const text = new TextDecoder(encoding).decode(buf).replace(/^﻿/, '');
    return XLSX.read(text, { type: 'string', cellDates: true });
  };

  const parseFile = async (f) => {
    setParseError('');
    setDuplicateInfo({ existing: 0, inFile: 0 });
    setIsChecking(true);
    try {
      const wb = await readWorkbook(f);
      const sheetName = wb.SheetNames[0];
      const sheet = wb.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: true });

      if (!json.length) {
        setParseError('Excel dosyası boş görünüyor.');
        setRows([]);
        return;
      }

      const firstKeys = Object.keys(json[0]).map(k => k.trim());
      const hasRequired = firstKeys.some(k => k === 'TUTAR') &&
                          firstKeys.some(k => k === 'GÖNDERİCİ UNVAN');
      if (!hasRequired) {
        setParseError('Bu dosya beklenen gider formatında değil. TUTAR ve GÖNDERİCİ UNVAN sütunları bulunamadı.');
        setRows([]);
        return;
      }

      const mapped = json.map((r) => {
        const get = (key) => {
          if (r[key] !== undefined) return r[key];
          const match = Object.keys(r).find(k => k.trim() === key);
          return match ? r[match] : '';
        };
        const str = (key) => decodeHtmlEntities(String(get(key) ?? '')).trim();
        const gonderici = str('GÖNDERİCİ UNVAN');
        const gib = str('GİB NUMARASI');
        const tutar = parseAmount(get('TUTAR'));
        const paraBirimi = str('PARA BİRİMİ') || 'TRY';
        const belgeTarihi = parseDate(get('BELGE TARİHİ')) || parseDate(get('OLUŞTURMA TARİHİ'));
        return {
          BütçeAdı: gib ? `${gonderici} - ${gib}` : gonderici || 'E-Fatura Gider',
          BütçeKaynak: gonderici,
          BütçeSahibi: 'Şirket',
          BütçeTipi: 'Gider',
          GiderTipi: 'E-Fatura',
          BütçeKategorisi: 'E-Fatura',
          ParaBirimi: paraBirimi,
          KDV: '0',
          BütçeMiktarı: tutar,
          BütçeTarihi: belgeTarihi,
          BeklenenÖdemeTarihi: belgeTarihi,
          ÖdemeDurumu: 'Beklemede',
          AktifDurum: 'Aktif',
          GibNumarasi: gib,
          TaslakNo: str('TASLAK NO'),
          Senaryo: str('SENARYO'),
          FaturaTipi: str('FATURA TİPİ'),
          OlusturmaTarihi: parseDate(get('OLUŞTURMA TARİHİ')),
          OlusturmaZamani: parseTime(get('OLUŞTURMA ZAMANI')),
          BelgeTarihi: parseDate(get('BELGE TARİHİ')),
          BelgeZamani: parseTime(get('BELGE ZAMANI')),
          GondericiTcknVkn: str('GÖNDERİCİ TCKN/VKN'),
          GondericiUnvan: gonderici,
          GondericiEtiketi: str('GÖNDERİCİ ETİKETİ'),
          Tutar: tutar,
          ZarfUuid: str('ZARF UUID'),
          ZarfDurumu: str('ZARF DURUMU'),
          Ettn: str('ETTN'),
          Iskonto: parseAmount(get('İSKONTO')),
          FaturaDisiMasraflari: parseAmount(get('FATURA DIŞI MASRAFLARI')),
          SatirSayisi: parseAmount(get('SATIR SAYISI')),
          BirimFiyatMiktarTutari: parseAmount(get('BİRİM FİYAT * MİKTAR TUTARI')),
          OdenecekTutar: parseAmount(get('ÖDENECEK TUTAR')),
          VergilerHaricTutar: parseAmount(get('VERGİLER HARİÇ TUTAR')),
          VergilerDahilTutar: parseAmount(get('VERGİLER DAHİL TUTAR')),
        };
      });
      // Skip rows that already exist in Firestore as an exact match (all fields identical),
      // and rows that repeat inside the same file.
      const { newRows, existingDuplicates, inFileDuplicates } = await splitDuplicateRows(mapped);
      setDuplicateInfo({ existing: existingDuplicates, inFile: inFileDuplicates });
      setRows(newRows);
    } catch (err) {
      console.error(err);
      setParseError('Excel dosyası okunurken veya mevcut kayıtlarla karşılaştırılırken bir hata oluştu.');
      setRows([]);
    } finally {
      setIsChecking(false);
    }
  };

  const handleFile = (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      toast.error('Lütfen geçerli bir Excel dosyası seçin (xlsx, xls, csv).');
      return;
    }
    setFile(f);
    parseFile(f);
  };

  const handleFileInputChange = (e) => {
    handleFile(e.target.files?.[0]);
    e.target.value = '';
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleImport = async () => {
    if (!rows.length) return;
    setIsImporting(true);
    setProgress({ done: 0, total: rows.length });
    try {
      // Re-check right before writing so records added since the file was parsed are not duplicated either.
      const { newRows: toWrite, existingDuplicates } = await splitDuplicateRows(rows);
      const skipped = duplicateInfo.existing + existingDuplicates;
      if (!toWrite.length) {
        toast.info('Tüm satırlar zaten kayıtlı. Yeni kayıt eklenmedi.');
        onClose();
        return;
      }
      setProgress({ done: 0, total: toWrite.length });
      const chunkSize = 400;
      const createdAt = new Date().toISOString();
      let done = 0;
      for (let i = 0; i < toWrite.length; i += chunkSize) {
        const chunk = toWrite.slice(i, i + chunkSize);
        const batch = writeBatch(db);
        chunk.forEach((row) => {
          const ref = doc(collection(db, 'Budgets'));
          batch.set(ref, { ...row, createdAt, importSource: 'excel-gider' });
        });
        await batch.commit();
        done += chunk.length;
        setProgress({ done, total: toWrite.length });
      }
      toast.success(`${toWrite.length} gider kaydı başarıyla içe aktarıldı.${skipped ? ` ${skipped} satır zaten kayıtlı olduğu için atlandı.` : ''}`);
      onImported && onImported();
      onClose();
    } catch (err) {
      console.error('Import error:', err);
      toast.error('İçe aktarma sırasında bir hata oluştu.');
    } finally {
      setIsImporting(false);
    }
  };

  if (!isOpen) return null;

  const totalAmount = rows.reduce((s, r) => s + (parseFloat(r.BütçeMiktarı) || 0), 0);

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Excel'den Gider İçe Aktar</h2>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close modal">
            <FaTimes />
          </button>
        </div>

        <div className={styles.modalBody}>
          {!file && (
            <div
              className={`${styles.dropZone} ${isDragging ? styles.dropZoneDragging : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current.click()}
            >
              <FaUpload className={styles.uploadIcon} />
              <p className={styles.dropZoneText}>Gelen E-Fatura Excel dosyasını buraya sürükleyin veya tıklayın</p>
              <p className={styles.dropZoneHint}>Desteklenen formatlar: .xlsx, .xls, .csv</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileInputChange}
                className={styles.hiddenInput}
              />
            </div>
          )}

          {file && (
            <div style={{ marginBottom: 16 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: 12, border: '1px solid var(--border-color)',
                borderRadius: 8, background: 'var(--hover-bg)'
              }}>
                <FaFileExcel style={{ color: '#2F855A', fontSize: 24 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{file.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-light)' }}>
                    {(file.size / 1024).toFixed(1)} KB
                  </div>
                </div>
                <button
                  type="button"
                  onClick={resetState}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--danger-color)' }}
                  disabled={isImporting}
                  title="Dosyayı kaldır"
                >
                  <FaTrash />
                </button>
              </div>

              {parseError && (
                <div style={{
                  marginTop: 12, padding: 12, borderRadius: 8,
                  background: '#FFF5F5', color: '#C53030',
                  display: 'flex', alignItems: 'center', gap: 8
                }}>
                  <FaExclamationTriangle /> {parseError}
                </div>
              )}

              {!parseError && rows.length > 0 && (
                <>
                  <div style={{
                    marginTop: 12, padding: 12, borderRadius: 8,
                    background: '#F0FFF4', color: '#2F855A',
                    display: 'flex', alignItems: 'center', gap: 8
                  }}>
                    <FaCheckCircle /> {rows.length} yeni satır içe aktarılacak. Toplam tutar: {totalAmount.toLocaleString('tr-TR')} ₺
                  </div>

                  <div style={{
                    marginTop: 12, maxHeight: 260, overflow: 'auto',
                    border: '1px solid var(--border-color)', borderRadius: 8
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-secondary, #f5f5f5)' }}>
                        <tr>
                          <th style={{ padding: 8, textAlign: 'left' }}>Gönderici</th>
                          <th style={{ padding: 8, textAlign: 'left' }}>Tarih</th>
                          <th style={{ padding: 8, textAlign: 'right' }}>Tutar</th>
                          <th style={{ padding: 8, textAlign: 'left' }}>PB</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.slice(0, 50).map((r, i) => (
                          <tr key={i} style={{ borderTop: '1px solid var(--border-color)' }}>
                            <td style={{ padding: 8 }}>{r.BütçeKaynak}</td>
                            <td style={{ padding: 8 }}>{r.BütçeTarihi}</td>
                            <td style={{ padding: 8, textAlign: 'right' }}>
                              {Number(r.BütçeMiktarı).toLocaleString('tr-TR')}
                            </td>
                            <td style={{ padding: 8 }}>{r.ParaBirimi}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {rows.length > 50 && (
                      <div style={{ padding: 8, textAlign: 'center', color: 'var(--text-light)', fontSize: 12 }}>
                        ...ve {rows.length - 50} satır daha
                      </div>
                    )}
                  </div>
                </>
              )}

              {isChecking && (
                <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-light)' }}>
                  Mevcut kayıtlarla karşılaştırılıyor...
                </div>
              )}

              {!parseError && !isChecking && (duplicateInfo.existing > 0 || duplicateInfo.inFile > 0) && (
                <div style={{
                  marginTop: 12, padding: 12, borderRadius: 8,
                  background: '#FFFAF0', color: '#975A16',
                  display: 'flex', alignItems: 'center', gap: 8
                }}>
                  <FaExclamationTriangle />
                  <span>
                    {duplicateInfo.existing > 0 && `${duplicateInfo.existing} satır mevcut kayıtlarla birebir aynı olduğu için atlanacak. `}
                    {duplicateInfo.inFile > 0 && `${duplicateInfo.inFile} satır dosya içinde tekrar ettiği için atlanacak. `}
                    {rows.length === 0 && 'Eklenecek yeni satır yok.'}
                  </span>
                </div>
              )}

              {isImporting && (
                <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-light)' }}>
                  İçe aktarılıyor: {progress.done} / {progress.total}
                </div>
              )}
            </div>
          )}

          <div className={styles.buttonGroup}>
            <button
              type="button"
              className={`${styles.button} ${styles.cancelButton}`}
              onClick={onClose}
              disabled={isImporting}
            >
              İptal
            </button>
            <button
              type="button"
              className={`${styles.button} ${styles.submitButton}`}
              onClick={handleImport}
              disabled={!rows.length || !!parseError || isImporting || isChecking}
            >
              {isImporting ? `İçe Aktarılıyor...` : `İçe Aktar${rows.length ? ` (${rows.length})` : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportExpenseExcelModal;
